import auth0Client from "@/auth0";
import { changeLanguage } from "@/i18n/i18n";
import { queryClient } from "@/root";
import { getGetMeQueryKey, getMe } from "@/spz-api/user/user";
import { datadogRum } from "@datadog/browser-rum";
import { redirect } from "react-router-dom";

export const protectedLoader = async ({ request }: { request: Request }) => {
  const url = new URL(request.url);
  const hasCode = url.searchParams.has("code");
  const hasState = url.searchParams.has("state");
  const hasError = url.searchParams.has("error");
  const hasInvitation = url.searchParams.has("invitation");
  const hasOrganization = url.searchParams.has("organization");

  // Handle Auth0 errors first
  if (hasError) {
    const errorDescription = url.searchParams.get("error_description");
    datadogRum.addError(new Error("Auth0 Error Callback"), {
      source: "auth0",
      errorDescription: decodeURIComponent(errorDescription ?? ""),
    });
    throw redirect(
      `/access-denied?error=${hasError}&error_description=${errorDescription}`,
    );
  }

  // Handle invitation links BEFORE callback (user not authenticated yet)
  if (hasInvitation && hasOrganization && !hasCode) {
    const returnTo = url.pathname;
    await auth0Client.loginWithRedirect({
      returnTo,
      invitation: url.searchParams.get("invitation"),
      organization: url.searchParams.get("organization"),
    });
    // Never continue execution after loginWithRedirect
    return new Promise(() => {}); // Pending promise to prevent loader from continuing
  }

  // Handle Auth0 callback (after redirect from Auth0)
  if (hasCode && hasState) {
    try {
      const result = await auth0Client.handleRedirectCallback();
      // Redirect to clean URL (remove code/state from URL)
      const returnTo = result.appState?.returnTo ?? url.pathname;
      return redirect(returnTo);
    } catch (error) {
      datadogRum.addError(error);
      return redirect("/access-denied");
    }
  }

  const isAuthenticated = await auth0Client.isAuthenticated();

  if (!isAuthenticated) {
    // Preserve query params except Auth0-specific ones (to avoid including old code/state)
    const searchParams = new URLSearchParams(url.search);
    searchParams.delete("code");
    searchParams.delete("state");
    searchParams.delete("error");
    searchParams.delete("error_description");

    const searchString = searchParams.toString();
    const returnTo = searchString
      ? `${url.pathname}?${searchString}`
      : url.pathname;

    await auth0Client.loginWithRedirect({ returnTo });
    // Never continue execution after loginWithRedirect
    return new Promise(() => {}); // Pending promise to prevent loader from continuing
  }

  const user = await queryClient.fetchQuery({
    queryKey: [getGetMeQueryKey()],
    queryFn: () => getMe(),
  });

  if (user?.preferredLanguage) {
    await changeLanguage(user?.preferredLanguage);
  }

  return null;
};
